/**
 * Meeting-level payroll outcome control.
 *
 * Shows every scheduled meeting in the pay period for one instructor + class,
 * with its normal compensation, recorded outcome, amount owed, and admin note.
 * Writes go to public.payroll_meeting_exclusions (the payroll engine's own
 * source of truth) — never to assignments, rates, or paid history.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { money } from "@/lib/payroll";
import {
  MEETING_OUTCOMES, listMeetingOutcomes, outcomeLabel, setMeetingOutcome,
  type MeetingOutcome,
} from "@/lib/meetingOutcomes";
import { Loader2, Pencil } from "lucide-react";

function fmt(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export interface MeetingRow {
  meeting_date: string;
  rate_cents: number;
}

export function MeetingOutcomePanel({
  instructorId, instructorName, classSlug, classTitle, sessionLabel, meetings, onChanged,
}: {
  instructorId: string;
  instructorName: string;
  classSlug: string;
  classTitle: string;
  sessionLabel: string | null;
  meetings: MeetingRow[];
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<MeetingRow | null>(null);
  const [outcome, setOutcome] = useState<MeetingOutcome>("taught_as_scheduled");
  const [note, setNote] = useState("");

  const { data: recorded = [] } = useQuery({
    queryKey: ["payroll", "meeting-outcomes", instructorId],
    queryFn: () => listMeetingOutcomes(instructorId),
  });

  const outcomeFor = (date: string) =>
    recorded.find((r) => r.class_slug === classSlug && r.meeting_date === date) ?? null;

  const save = useMutation({
    mutationFn: () =>
      setMeetingOutcome({
        instructorId, classSlug, sessionLabel,
        meetingDate: editing!.meeting_date,
        outcome, note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll", "meeting-outcomes", instructorId] });
      setEditing(null);
      toast({ title: "Meeting outcome saved", description: "Payroll for this meeting was recalculated." });
      onChanged();
    },
    onError: (e: any) =>
      toast({ title: "Couldn't save the outcome", description: e?.message ?? "Please try again.", variant: "destructive" }),
  });

  if (meetings.length === 0) return null;

  return (
    <div className="mt-2 space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Meeting outcomes</div>
      {meetings.map((m) => {
        const rec = outcomeFor(m.meeting_date);
        const owed = rec ? 0 : m.rate_cents;
        return (
          <div
            key={m.meeting_date}
            className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 ${rec ? "border-dashed bg-muted/40" : "bg-background"}`}
          >
            <span className={`font-medium ${rec ? "text-muted-foreground line-through" : "text-foreground"}`}>
              {fmt(m.meeting_date)}
            </span>
            <span className="text-muted-foreground">{classTitle}</span>
            <span className="text-muted-foreground">· {instructorName}</span>
            <span className="text-muted-foreground">· Normal rate {money(m.rate_cents)}</span>
            <Badge variant={rec ? "outline" : "secondary"} className="text-[10px]">
              {outcomeLabel(rec?.outcome)}
            </Badge>
            <span className={`font-semibold ${owed === 0 ? "text-muted-foreground" : "text-foreground"}`}>
              Payroll: {money(owed)}
            </span>
            {rec?.reason ? <span className="w-full text-muted-foreground">Note: {rec.reason}</span> : null}
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-7 gap-1 text-xs"
              onClick={() => {
                setEditing(m);
                setOutcome((rec?.outcome as MeetingOutcome) ?? "taught_as_scheduled");
                setNote(rec?.reason ?? "");
              }}
            >
              <Pencil className="h-3 w-3" /> Outcome
            </Button>
          </div>
        );
      })}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Meeting outcome</DialogTitle>
            <DialogDescription>
              {editing ? `${fmt(editing.meeting_date)} — ${classTitle}${sessionLabel ? ` · ${sessionLabel}` : ""} · ${instructorName}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={(v) => setOutcome(v as MeetingOutcome)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEETING_OUTCOMES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {MEETING_OUTCOMES.find((o) => o.value === outcome)?.help}
              </p>
              {outcome === "substitute_taught" && (
                <p className="text-xs text-muted-foreground">
                  Substitute pay is not automated. Record the substitute's pay separately as an adjustment.
                </p>
              )}
            </div>
            {outcome !== "taught_as_scheduled" && (
              <div className="space-y-1.5">
                <Label>
                  Admin note{outcome === "other" ? " (required)" : " (optional)"}
                </Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="What happened with this meeting?" />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              This changes payroll for this one meeting only. The meeting stays in history and the
              instructor's normal rate and assignment are unchanged.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || (outcome === "other" && !note.trim())}
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
